/**
 * Observabilidad y AI_Observability de la Capa Operacional AI-First.
 *
 * Implementa la interfaz {@link Obs} de `../api/contracts` para la
 * variante **demo** (client-side, R17.1/R17.5). En modo demo la
 * telemetría se mantiene en un **buffer en memoria** (el equivalente
 * client-side al "buffer en consola" descrito en el diseño); en el modo
 * conectado el mismo contrato se respalda con OpenTelemetry →
 * Prometheus/Grafana/Tempo/Loki.
 *
 * Responsabilidades de la tarea 10.1:
 *
 *   - {@link ObservabilidadLocal.trace}: asigna a cada Evento_Operacional
 *     o Accion_Semantica un `correlation_id` **único y reconstruible de
 *     extremo a extremo**, estable por entidad para permitir reconstruir
 *     su recorrido por los subsistemas (R14.2, R19.2). Toda recomendación
 *     o acción de IA se registra además con su **Explainability**
 *     (contexto consultado, entidades y Chunks de evidencia, razonamiento)
 *     y su **traza entrada–contexto–salida** (R19.1, R19.2, R19.3).
 *   - {@link ObservabilidadLocal.metric}: expone una métrica sobre una
 *     {@link Ventana}; cuando no es computable por ausencia de datos la
 *     marca como **dato no disponible** (`{ disponible: false }`),
 *     **distinguible de un 0** y **sin** originar incidente (R14.6).
 *   - {@link ObservabilidadLocal.raiseIncident}: emite una
 *     {@link SenalIncidente} correlacionable al superarse un umbral,
 *     marcando revisión humana para métricas de seguridad (R14.4, R20.5).
 *
 * El cálculo de las {@link AIMetrics} derivadas de la Memoria_Operacional
 * (acceptance_rate, false_positive, reversión) corresponde a la tarea
 * 10.2, que **extiende** {@link ObservabilidadLocal.aiMetrics}; aquí se
 * provee una implementación base determinista sobre las muestras
 * registradas para no romper el contrato ni el build.
 *
 * Las funciones puras {@link ventanaContiene}, {@link computarMetrica},
 * {@link excedeUmbral}, {@link requiereRevisionHumana} y {@link clamp01}
 * se exportan para que las pruebas PBT (Properties 37 y 38) las ejerciten
 * sin almacenamiento real ni temporizadores.
 */

import type {
  AIMetrics,
  EntidadRef,
  Id,
  IsoTimestamp,
  Obs,
  Result,
  SenalIncidente,
  ValorMetrica,
  Ventana,
} from "../api/contracts";
import { provideDemo, runtime } from "../api/runtime";
import { esAcierto, tieneLecturaEnlaces, type EnlacesMemoria } from "./memoria";

// ---------------------------------------------------------------------------
// Tipos del registro de Observabilidad
// ---------------------------------------------------------------------------

/**
 * Traza e2e de un Evento_Operacional o Accion_Semantica: vincula la
 * entidad con su `correlation_id` correlacionable a través de los
 * subsistemas (R14.2, R19.2).
 */
export interface TrazaObs {
  correlation_id: Id;
  evento_ref: EntidadRef;
  ts: IsoTimestamp;
}

/**
 * Explainability de una recomendación/acción de IA: contexto consultado,
 * entidades y Chunks de evidencia y razonamiento (R19.1).
 */
export interface ExplainabilityRegistro {
  /** Entidades del Grafo_Entidades consultadas para producir el resultado. */
  entidades: EntidadRef[];
  /** Chunks de la Base_Conocimiento_RAG usados como evidencia. */
  chunks: Id[];
  /** Razonamiento legible que fundamenta el resultado. */
  razonamiento: string;
}

/** Tipo de salida de IA trazada de extremo a extremo (R19.2, R19.3). */
export type TipoSalidaIA = "recomendacion" | "accion";

/**
 * Traza entrada–contexto–salida de una recomendación o acción de IA,
 * correlacionada por `correlation_id` (R19.2, R19.3).
 */
export interface TrazaIA {
  correlation_id: Id;
  tipo: TipoSalidaIA;
  /** Entrada que originó el resultado (intención, consulta, etc.). */
  entrada: unknown;
  /** Contexto recuperado + Explainability (R19.1). */
  contexto: ExplainabilityRegistro;
  /** Salida producida por la IA (recomendación o acción declarada). */
  salida: unknown;
  ts: IsoTimestamp;
}

/** Muestra de una métrica con marca temporal para filtrado por ventana. */
export interface MuestraMetrica {
  ts: IsoTimestamp;
  valor: number;
}

/** Incidente registrado por {@link ObservabilidadLocal.raiseIncident}. */
export interface IncidenteRegistrado extends SenalIncidente {
  correlation_id: Id;
  ts: IsoTimestamp;
}

/** Función de agregación de muestras en una ventana (default: media). */
export type Agregador = (valores: number[]) => number;

// ---------------------------------------------------------------------------
// Constantes de dominio
// ---------------------------------------------------------------------------

/**
 * Métricas de AI_Observability acotadas a `[0, 1]` (R14.5, R20.4). El
 * orden y los nombres coinciden con {@link AIMetrics}.
 */
export const METRICAS_IA = [
  "alucinacion",
  "retrieval_quality",
  "operator_trust",
  "acceptance_rate",
  "false_positive",
  "reversion",
] as const;

/**
 * Métricas cuya superación de umbral exige **revisión humana**
 * (reversión/alucinación altas) y señales de seguridad como un intento de
 * bypass bloqueado (R20.5, R12.8). El emparejamiento es por subcadena para
 * tolerar prefijos/sufijos (ej. "ia.alucinacion", "bypass_bloqueado").
 */
const METRICAS_REVISION_HUMANA: readonly string[] = [
  "alucinacion",
  "reversion",
  "bypass",
  "bloqueo",
  "bloqueado",
  "seguridad",
];

/**
 * Umbrales por defecto de cada métrica de AI_Observability sobre los que
 * se señala un incidente. Para métricas donde **más alto es peor**
 * (alucinación, false_positive, reversión) el umbral es un techo; para
 * métricas donde **más bajo es peor** (retrieval_quality, operator_trust,
 * acceptance_rate) se evalúa por su complemento (ver {@link superaUmbralIA}).
 * Configurables: las vistas/llamadores pueden sobreescribirlos.
 */
export const UMBRALES_IA: Readonly<Record<keyof AIMetrics, number>> = {
  alucinacion: 0.15,
  retrieval_quality: 0.6,
  operator_trust: 0.6,
  acceptance_rate: 0.5,
  false_positive: 0.2,
  reversion: 0.2,
};

/**
 * Métricas de AI_Observability donde un **valor alto** es indeseable: su
 * superación del umbral señala un incidente directamente. El resto son
 * métricas de calidad donde un valor **bajo** (por debajo del umbral) es
 * lo indeseable.
 */
const METRICAS_IA_ALTO_ES_MALO: ReadonlySet<keyof AIMetrics> = new Set([
  "alucinacion",
  "false_positive",
  "reversion",
]);

// ---------------------------------------------------------------------------
// Utilidades deterministas
// ---------------------------------------------------------------------------

/** Genera un identificador único con un prefijo legible. */
export function generarCorrelationId(prefijo = "corr"): Id {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === "function") return `${prefijo}-${c.randomUUID()}`;
  } catch {
    /* fallback abajo */
  }
  // Fallback sin Web Crypto (Node/Vitest sin crypto): aleatorio + tiempo.
  const rand = Math.random().toString(36).slice(2);
  return `${prefijo}-${Date.now().toString(36)}-${rand}`;
}

/** Clave estable de una referencia de entidad para correlación e2e. */
export function claveEntidad(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/** Acota un número al rango `[0, 1]` (NaN/no finito → 0). */
export function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** Convierte un ISO-8601 a epoch ms; devuelve NaN si no es parseable. */
function epoch(ts: IsoTimestamp): number {
  return Date.parse(ts);
}

/**
 * ¿Cae `ts` dentro de la {@link Ventana} `[desde, hasta]` (inclusive)?
 * Una ventana con límites no parseables o invertida (`desde > hasta`) no
 * contiene nada (determinista, sin lanzar).
 */
export function ventanaContiene(ventana: Ventana, ts: IsoTimestamp): boolean {
  const desde = epoch(ventana.desde);
  const hasta = epoch(ventana.hasta);
  const t = epoch(ts);
  if (!Number.isFinite(desde) || !Number.isFinite(hasta) || !Number.isFinite(t)) return false;
  if (desde > hasta) return false;
  return t >= desde && t <= hasta;
}

/** Media aritmética de un arreglo no vacío (agregador por defecto). */
export function media(valores: number[]): number {
  if (valores.length === 0) return 0;
  let suma = 0;
  for (const v of valores) suma += v;
  return suma / valores.length;
}

/**
 * Computa el {@link ValorMetrica} de un conjunto de muestras dentro de
 * una {@link Ventana}: si no hay muestras computables en la ventana
 * devuelve `{ disponible: false }` (dato no disponible, **≠ 0**, R14.6);
 * en otro caso agrega los valores finitos con `agg` (default: media).
 */
export function computarMetrica(
  muestras: ReadonlyArray<MuestraMetrica>,
  ventana: Ventana,
  agg: Agregador = media,
): ValorMetrica {
  const valores: number[] = [];
  for (const m of muestras) {
    if (!Number.isFinite(m.valor)) continue;
    if (ventanaContiene(ventana, m.ts)) valores.push(m.valor);
  }
  if (valores.length === 0) return { disponible: false };
  return { disponible: true, valor: agg(valores) };
}

/**
 * ¿El {@link ValorMetrica} supera estrictamente el `umbral`? Un dato
 * **no disponible** nunca supera el umbral, de modo que no origina una
 * señal de incidente derivada (R14.6).
 */
export function excedeUmbral(valor: ValorMetrica, umbral: number): boolean {
  if (!valor.disponible) return false;
  return valor.valor > umbral;
}

/**
 * Decide si una señal de incidente requiere **revisión humana**: cuando
 * la métrica pertenece al conjunto sensible de seguridad (alucinación,
 * reversión, intentos de bypass bloqueados) o cuando su valor supera el
 * umbral configurado (R20.5).
 */
export function requiereRevisionHumana(metrica: string, valor: number, umbral: number): boolean {
  const m = metrica.toLowerCase();
  if (METRICAS_REVISION_HUMANA.some((s) => m.includes(s))) return true;
  return valor > umbral;
}

// ---------------------------------------------------------------------------
// Derivación de métricas de IA desde los enlaces rec·dec·outcome (R14.5, R20.4)
// ---------------------------------------------------------------------------

/**
 * Deriva, de forma **determinista** y acotada a `[0, 1]`, la AI
 * acceptance rate, la tasa de falsos positivos y la tasa de reversión a
 * partir de los enlaces recomendación·decisión·outcome de la
 * Memoria_Operacional (R14.5, R20.4):
 *
 *   - **acceptance_rate** = recomendaciones `aceptada` ÷ recomendaciones
 *     resueltas (`aceptada` + `rechazada`). No computable si no hay
 *     recomendaciones resueltas en la ventana.
 *   - **false_positive** = outcomes **no acierto** enlazados a una
 *     recomendación **aceptada** ÷ total de outcomes enlazados a
 *     recomendaciones aceptadas. No computable si no hay tales outcomes.
 *   - **reversion** = outcomes cuyo `resultado` indica reversión ÷ total
 *     de outcomes. No computable si no hay outcomes en la ventana.
 *
 * Cada métrica se devuelve como {@link ValorMetrica}: no computable por
 * ausencia de datos ⇒ `{ disponible: false }`, distinguible de un 0
 * (R14.6).
 */
export interface MetricasDerivadasMemoria {
  acceptance_rate: ValorMetrica;
  false_positive: ValorMetrica;
  reversion: ValorMetrica;
}

/** Subcadenas de `resultado` que se interpretan como una reversión. */
const RESULTADOS_REVERSION: readonly string[] = ["revert", "revers", "deshace", "deshech", "rollback"];

function esReversion(resultado: string): boolean {
  const r = resultado.trim().toLowerCase();
  return RESULTADOS_REVERSION.some((s) => r.includes(s));
}

export function derivarMetricasMemoria(enlaces: EnlacesMemoria): MetricasDerivadasMemoria {
  // acceptance_rate: aceptadas / (aceptadas + rechazadas).
  let aceptadas = 0;
  let resueltas = 0;
  const idAceptadas = new Set<Id>();
  for (const { rec, estado } of enlaces.recomendaciones) {
    if (estado === "aceptada") {
      aceptadas += 1;
      resueltas += 1;
      idAceptadas.add(rec.recomendacion_id);
    } else if (estado === "rechazada") {
      resueltas += 1;
    }
  }
  const acceptance_rate: ValorMetrica =
    resueltas > 0 ? { disponible: true, valor: clamp01(aceptadas / resueltas) } : { disponible: false };

  // false_positive: outcomes no-acierto sobre recomendaciones aceptadas / outcomes sobre aceptadas.
  let outcomesSobreAceptadas = 0;
  let falsosPositivos = 0;
  // reversion: outcomes con resultado de reversión / total outcomes.
  let totalOutcomes = 0;
  let reversiones = 0;
  for (const { outcome, origen_ref } of enlaces.outcomes) {
    totalOutcomes += 1;
    if (esReversion(outcome.resultado ?? "")) reversiones += 1;
    if (idAceptadas.has(origen_ref.id)) {
      outcomesSobreAceptadas += 1;
      if (!esAcierto(outcome)) falsosPositivos += 1;
    }
  }
  const false_positive: ValorMetrica =
    outcomesSobreAceptadas > 0
      ? { disponible: true, valor: clamp01(falsosPositivos / outcomesSobreAceptadas) }
      : { disponible: false };
  const reversion: ValorMetrica =
    totalOutcomes > 0 ? { disponible: true, valor: clamp01(reversiones / totalOutcomes) } : { disponible: false };

  return { acceptance_rate, false_positive, reversion };
}

/**
 * ¿La métrica de IA `nombre` con valor `valor` supera su umbral en el
 * sentido indeseable? Para métricas donde "alto es malo" (alucinación,
 * false_positive, reversión) supera cuando `valor > umbral`; para
 * métricas de calidad supera cuando `valor < umbral` (calidad baja).
 */
export function superaUmbralIA(nombre: keyof AIMetrics, valor: number, umbral: number): boolean {
  if (METRICAS_IA_ALTO_ES_MALO.has(nombre)) return valor > umbral;
  return valor < umbral;
}

// ---------------------------------------------------------------------------
// Reporte de AI_Observability para la UI (estado disponible/no disponible)
// ---------------------------------------------------------------------------

/** Estado de una métrica de IA para presentación en la vista. */
export interface EstadoMetricaIA {
  nombre: keyof AIMetrics;
  /** Valor computable acotado a `[0, 1]`, o no disponible (R14.6). */
  valor: ValorMetrica;
  /** Umbral configurado para esta métrica. */
  umbral: number;
  /** True si la métrica computable supera el umbral en sentido indeseable. */
  excede: boolean;
  /** True si la superación exige revisión humana (R20.5). */
  revision_humana: boolean;
}

/**
 * Reporte completo de AI_Observability: las seis métricas con su estado
 * (disponible/no disponible) y resaltado de las que superan umbral
 * (R14.3, R14.5, R20.4, R20.5).
 */
export interface ReporteAIObservability {
  ventana: Ventana;
  metricas: EstadoMetricaIA[];
  /** True si alguna métrica de seguridad superó umbral (R20.5). */
  incidente_revision_humana: boolean;
  /** True si los enlaces de la Memoria no estuvieron disponibles. */
  memoria_degradada: boolean;
}

// ---------------------------------------------------------------------------
// ObservabilidadLocal — implementación demo de la interfaz Obs
// ---------------------------------------------------------------------------

/** Opciones de construcción de {@link ObservabilidadLocal}. */
export interface ObservabilidadOptions {
  /** Reloj inyectable (ms epoch) para trazas deterministas en tests. */
  reloj?: () => number;
  /** Generador de correlation_id inyectable para tests deterministas. */
  generarId?: (prefijo?: string) => Id;
}

/**
 * Observabilidad/AI_Observability client-side que implementa {@link Obs}.
 *
 * Mantiene buffers en memoria de trazas e2e, trazas de IA, muestras de
 * métricas e incidentes. El `correlation_id` es **estable por entidad**:
 * trazar dos veces la misma `evento_ref` devuelve el mismo identificador,
 * habilitando la reconstrucción del recorrido completo (R14.2, R19.2).
 */
export class ObservabilidadLocal implements Obs {
  private readonly reloj: () => number;
  private readonly genId: (prefijo?: string) => Id;

  /** correlation_id estable por entidad (clave → id). */
  private readonly correlPorEntidad = new Map<string, Id>();
  /** Trazas e2e por correlation_id. */
  private readonly trazas = new Map<Id, TrazaObs>();
  /** Trazas entrada–contexto–salida de recomendaciones/acciones de IA. */
  private readonly trazasIA = new Map<Id, TrazaIA>();
  /** Muestras de métricas por nombre. */
  private readonly muestras = new Map<string, MuestraMetrica[]>();
  /** Incidentes emitidos, en orden de emisión. */
  private readonly incidentes: IncidenteRegistrado[] = [];

  constructor(opts?: ObservabilidadOptions) {
    this.reloj = opts?.reloj ?? (() => Date.now());
    this.genId = opts?.generarId ?? generarCorrelationId;
  }

  private ahora(): IsoTimestamp {
    return new Date(this.reloj()).toISOString();
  }

  /**
   * Asigna (o recupera) el `correlation_id` único de un Evento_Operacional
   * o Accion_Semantica, reconstruible de extremo a extremo. Idempotente
   * por entidad: la misma `evento_ref` conserva su identificador a lo
   * largo de los subsistemas (R14.2, R19.2).
   */
  async trace(evento_ref: EntidadRef): Promise<Result<Id>> {
    try {
      if (!evento_ref || typeof evento_ref.id !== "string" || typeof evento_ref.tipo !== "string") {
        return { ok: false, error: "trace requiere una EntidadRef válida (tipo, id).", degraded: false };
      }
      const clave = claveEntidad(evento_ref);
      const existente = this.correlPorEntidad.get(clave);
      if (existente) return { ok: true, value: existente };

      const correlation_id = this.genId("corr");
      this.correlPorEntidad.set(clave, correlation_id);
      this.trazas.set(correlation_id, { correlation_id, evento_ref, ts: this.ahora() });
      return { ok: true, value: correlation_id };
    } catch (err) {
      return { ok: false, error: `No se pudo trazar el evento: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Registra una recomendación o acción de IA con su Explainability y su
   * traza entrada–contexto–salida, correlacionada e2e (R19.1, R19.2,
   * R19.3). Reutiliza el `correlation_id` estable de la entidad asociada.
   * Devuelve el `correlation_id` con que quedó registrada.
   */
  async traceIA(
    evento_ref: EntidadRef,
    tipo: TipoSalidaIA,
    entrada: unknown,
    contexto: ExplainabilityRegistro,
    salida: unknown,
  ): Promise<Result<Id>> {
    try {
      const r = await this.trace(evento_ref);
      if (!r.ok) return r;
      const correlation_id = r.value;
      this.trazasIA.set(correlation_id, {
        correlation_id,
        tipo,
        entrada,
        contexto,
        salida,
        ts: this.ahora(),
      });
      return { ok: true, value: correlation_id };
    } catch (err) {
      return { ok: false, error: `No se pudo trazar la IA: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Registra una muestra de una métrica para su posterior agregación por
   * ventana. Pensado para que los motores de la Capa alimenten la
   * Observabilidad sin acoplarse a su almacenamiento.
   */
  recordMetric(nombre: string, valor: number, ts?: IsoTimestamp): void {
    if (typeof nombre !== "string" || nombre.length === 0) return;
    const lista = this.muestras.get(nombre) ?? [];
    lista.push({ ts: ts ?? this.ahora(), valor });
    this.muestras.set(nombre, lista);
  }

  /**
   * Expone el valor de una métrica sobre una ventana. Si no hay datos
   * computables en la ventana, la marca como **dato no disponible**
   * (`{ disponible: false }`), distinguible de un 0 y **sin** emitir
   * incidente (R14.6).
   */
  async metric(nombre: string, ventana: Ventana): Promise<Result<ValorMetrica>> {
    try {
      if (typeof nombre !== "string" || nombre.length === 0) {
        return { ok: false, error: "metric requiere un nombre de métrica válido.", degraded: false };
      }
      const muestras = this.muestras.get(nombre) ?? [];
      return { ok: true, value: computarMetrica(muestras, ventana) };
    } catch (err) {
      return { ok: false, error: `No se pudo computar la métrica: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Métricas de AI_Observability en `[0, 1]` (R14.5, R20.4). Las métricas
   * **acceptance_rate**, **false_positive** y **reversión** se **derivan
   * de los enlaces recomendación·decisión·outcome** de la
   * Memoria_Operacional (R14.5, R20.4), leídos vía `runtime.memoria()`
   * cuando expone la capacidad de lectura de enlaces; el resto
   * (alucinación, retrieval_quality, operator_trust) se agregan de las
   * muestras registradas. Si la Memoria no está disponible se degrada con
   * elegancia a las muestras de métrica registradas (R17.6). Toda métrica
   * no computable se reporta como 0 acotado en este contrato; el detalle
   * disponible/no disponible lo expone {@link ObservabilidadLocal.reporteAI}.
   */
  async aiMetrics(ventana: Ventana): Promise<Result<AIMetrics>> {
    try {
      const valorMuestra = (nombre: string): number => {
        const v = computarMetrica(this.muestras.get(nombre) ?? [], ventana);
        return v.disponible ? clamp01(v.valor) : 0;
      };

      const derivadas = await this.derivarDeMemoria(ventana);
      const valorDerivado = (md: ValorMetrica, nombre: string): number =>
        md.disponible ? clamp01(md.valor) : valorMuestra(nombre);

      const metrics: AIMetrics = {
        alucinacion: valorMuestra("alucinacion"),
        retrieval_quality: valorMuestra("retrieval_quality"),
        operator_trust: valorMuestra("operator_trust"),
        acceptance_rate: derivadas
          ? valorDerivado(derivadas.acceptance_rate, "acceptance_rate")
          : valorMuestra("acceptance_rate"),
        false_positive: derivadas
          ? valorDerivado(derivadas.false_positive, "false_positive")
          : valorMuestra("false_positive"),
        reversion: derivadas ? valorDerivado(derivadas.reversion, "reversion") : valorMuestra("reversion"),
      };
      return { ok: true, value: metrics };
    } catch (err) {
      return { ok: false, error: `No se pudieron computar las métricas de IA: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Lee los enlaces rec·dec·outcome de la Memoria_Operacional vía
   * `runtime.memoria()` y deriva las métricas dependientes de ella
   * (R14.5, R20.4). Devuelve `null` si la Memoria no expone la capacidad
   * de lectura o si la lectura falla (degradación elegante, R17.6).
   */
  private async derivarDeMemoria(ventana: Ventana): Promise<MetricasDerivadasMemoria | null> {
    try {
      const memoria: unknown = runtime.memoria();
      if (!tieneLecturaEnlaces(memoria)) return null;
      const r = await memoria.enlaces(ventana);
      if (!r.ok) return null;
      return derivarMetricasMemoria(r.value);
    } catch {
      return null;
    }
  }

  /**
   * Construye el {@link ReporteAIObservability} para la vista: las seis
   * métricas con su estado **disponible/no disponible** (R14.6) y el
   * resaltado de las que superan umbral (R14.3, R14.5, R20.4). Cuando una
   * métrica sensible de seguridad (alucinación/reversión) supera su umbral
   * emite además una señal de incidente dirigida a revisión humana (R20.5).
   */
  async reporteAI(
    ventana: Ventana,
    umbrales: Readonly<Record<keyof AIMetrics, number>> = UMBRALES_IA,
  ): Promise<ReporteAIObservability> {
    const derivadas = await this.derivarDeMemoria(ventana);
    const memoria_degradada = derivadas === null;

    const valorMuestra = (nombre: string): ValorMetrica =>
      computarMetrica(this.muestras.get(nombre) ?? [], ventana);

    const valores: Record<keyof AIMetrics, ValorMetrica> = {
      alucinacion: valorMuestra("alucinacion"),
      retrieval_quality: valorMuestra("retrieval_quality"),
      operator_trust: valorMuestra("operator_trust"),
      acceptance_rate: derivadas ? derivadas.acceptance_rate : valorMuestra("acceptance_rate"),
      false_positive: derivadas ? derivadas.false_positive : valorMuestra("false_positive"),
      reversion: derivadas ? derivadas.reversion : valorMuestra("reversion"),
    };

    let incidente_revision_humana = false;
    const metricas: EstadoMetricaIA[] = [];
    for (const nombre of METRICAS_IA) {
      const valor = valores[nombre];
      const umbral = umbrales[nombre];
      const excede = valor.disponible ? superaUmbralIA(nombre, valor.valor, umbral) : false;
      const sensible = nombre === "alucinacion" || nombre === "reversion";
      const revision_humana = excede && sensible;
      if (revision_humana) {
        incidente_revision_humana = true;
        // Señal de incidente a revisión humana al superar umbral (R20.5).
        await this.raiseIncident(nombre, umbral);
      }
      metricas.push({ nombre, valor, umbral, excede, revision_humana });
    }

    return { ventana, metricas, incidente_revision_humana, memoria_degradada };
  }

  /**
   * Emite una {@link SenalIncidente} correlacionable al superarse un
   * umbral (R14.4). El valor reportado es el de la última muestra agregada
   * de la métrica si existe; si no hay dato numérico (p. ej. una señal de
   * seguridad como un bypass bloqueado) se reporta al nivel del umbral. La
   * revisión humana se activa para métricas sensibles de seguridad o
   * cuando el valor supera el umbral (R20.5).
   */
  async raiseIncident(metrica: string, umbral: number): Promise<Result<SenalIncidente>> {
    try {
      if (typeof metrica !== "string" || metrica.length === 0) {
        return { ok: false, error: "raiseIncident requiere un nombre de métrica válido.", degraded: false };
      }
      const muestras = this.muestras.get(metrica) ?? [];
      const ultima = muestras.length > 0 ? muestras[muestras.length - 1].valor : undefined;
      const valor = typeof ultima === "number" && Number.isFinite(ultima) ? ultima : umbral;

      const senal: SenalIncidente = {
        metrica,
        umbral,
        valor,
        revision_humana: requiereRevisionHumana(metrica, valor, umbral),
      };
      this.incidentes.push({ ...senal, correlation_id: this.genId("inc"), ts: this.ahora() });
      return { ok: true, value: senal };
    } catch (err) {
      return { ok: false, error: `No se pudo emitir el incidente: ${mensajeError(err)}`, degraded: true };
    }
  }

  // -------------------------------------------------------------------------
  // Utilidades de diagnóstico/reconstrucción (R19.4 — trazabilidad)
  // -------------------------------------------------------------------------

  /** Recupera la traza e2e asociada a un `correlation_id`. */
  getTraza(correlation_id: Id): TrazaObs | undefined {
    return this.trazas.get(correlation_id);
  }

  /** Recupera la traza entrada–contexto–salida de IA por `correlation_id`. */
  getTrazaIA(correlation_id: Id): TrazaIA | undefined {
    return this.trazasIA.get(correlation_id);
  }

  /** Incidentes emitidos (uso de diagnóstico/test). */
  getIncidentes(): ReadonlyArray<IncidenteRegistrado> {
    return [...this.incidentes];
  }

  /** Conteos de cada buffer (uso de diagnóstico/test). */
  size(): { trazas: number; trazasIA: number; metricas: number; incidentes: number } {
    return {
      trazas: this.trazas.size,
      trazasIA: this.trazasIA.size,
      metricas: this.muestras.size,
      incidentes: this.incidentes.length,
    };
  }
}

function mensajeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------------------
// Singleton + auto-registro en el runtime selector (patrón realtimeHub/contextoIA)
// ---------------------------------------------------------------------------

let instancia: ObservabilidadLocal | null = null;
let registrado = false;

/** Devuelve (creándola si hace falta) la Observabilidad demo. */
export function getObservabilidad(): ObservabilidadLocal {
  if (!instancia) instancia = new ObservabilidadLocal();
  return instancia;
}

/**
 * Registra (una sola vez) la implementación DEMO de la Observabilidad en
 * el runtime selector, de modo que las vistas y la IA la consuman SIEMPRE
 * vía `runtime.obs()` (R17.1, R17.2, R17.5) y nunca la instancien
 * directamente. Idempotente y seguro de llamar múltiples veces.
 */
export function ensureObsProvider(): void {
  if (registrado) return;
  registrado = true;
  provideDemo("obs", () => getObservabilidad());
}

// Auto-registro al importar el módulo: cualquier consumidor (p. ej. el
// Motor_Contexto_IA, que invoca `runtime.obs().raiseIncident(...)`) que
// importe este módulo deja el proveedor DEMO disponible en el runtime
// selector sin tocar las vistas ni el selector.
ensureObsProvider();
